<script setup lang="ts">
/**
 * Allotment of Shares (OTC) — 12문서 패키지를 작성 흐름 4단계(스텝퍼)로 렌더.
 * · 모든 문서를 마운트 유지(v-show) → 단계 이동해도 입력값 보존. 필드 키는 문서 간 공유(ctx values).
 * · Stage 2 청약자 유형(Individual/Corporate)에 따라 청약 문서 하나만 노출.
 */
import { computed, inject, ref, nextTick } from 'vue';
import type { Component } from 'vue';
import { OTC_DOC_CTX } from './otcContext';
import { OTC_DOCS, OTC_STAGES, inferAllotteeCategory, type OtcDocCode } from './otcFields';
import OtcWaiverDoc from './OtcWaiverDoc.vue';
import OtcAuthorityDoc from './OtcAuthorityDoc.vue';
import OtcPricingDoc from './OtcPricingDoc.vue';
import OtcSubscriptionIndividualDoc from './OtcSubscriptionIndividualDoc.vue';
import OtcSubscriptionCorporateDoc from './OtcSubscriptionCorporateDoc.vue';
import OtcSubscriptionDoc from './OtcSubscriptionDoc.vue';
import OtcLetterDoc from './OtcLetterDoc.vue';
import OtcIncreaseCapitalDoc from './OtcIncreaseCapitalDoc.vue';
import OtcS76DirectorDoc from './OtcS76DirectorDoc.vue';
import OtcS76SecretaryDoc from './OtcS76SecretaryDoc.vue';
import OtcStatementDoc from './OtcStatementDoc.vue';
import OtcS78ReturnDoc from './OtcS78ReturnDoc.vue';
import OtcRocCoverDoc from './OtcRocCoverDoc.vue';

const ctx = inject(OTC_DOC_CTX)!;

const DOC_COMPONENTS: Record<OtcDocCode, Component> = {
  TMP080: OtcWaiverDoc,
  TMP091: OtcAuthorityDoc,
  TMP088: OtcPricingDoc,
  TMP081: OtcSubscriptionIndividualDoc,
  TMP082: OtcSubscriptionCorporateDoc,
  TMP089: OtcLetterDoc,
  TMP090: OtcIncreaseCapitalDoc,
  TMP086: OtcS76DirectorDoc,
  TMP085: OtcS76SecretaryDoc,
  TMP083: OtcStatementDoc,
  TMP084: OtcS78ReturnDoc,
  TMP087: OtcRocCoverDoc,
};

const currentStage = ref(1);
const rootEl = ref<HTMLElement>();
/** 스텝 이동(Next/Previous) 시 스텝퍼가 보이도록 상단으로 스크롤. */
function scrollToTop() {
  nextTick(() => {
    const el = rootEl.value;
    if (!el) return;
    // 스크롤되는 조상(스튜디오 본문 등)을 찾아 최상단으로. 없으면 스텝퍼를 뷰포트 상단에 맞춤.
    let p: HTMLElement | null = el.parentElement;
    while (p) {
      const oy = getComputedStyle(p).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) {
        p.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      p = p.parentElement;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}
function go(n: number) {
  if (n < 1 || n > OTC_STAGES.length) return;
  currentStage.value = n;
  scrollToTop();
}

/** 검증 다이얼로그에서 미입력 항목 클릭 → 해당 stage 로 전환 후 필드로 스크롤 + 잠깐 강조. */
function focusField(key: string, stage: number) {
  if (stage >= 1 && stage <= OTC_STAGES.length) currentStage.value = stage;
  nextTick(() => {
    const sel = `[data-otc-field="${(window.CSS && CSS.escape) ? CSS.escape(key) : key}"]`;
    const els = Array.from(document.querySelectorAll(sel)) as HTMLElement[];
    const target = els.find((e) => e.offsetParent !== null) || els[0];
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('otc-flash');
    setTimeout(() => target.classList.remove('otc-flash'), 1600);
  });
}
defineExpose({ focusField });

/** Subscription(TMP081/082)은 allottee 별 별도 렌더 → 일반 문서 목록에서 제외. */
const view = computed(() => {
  let n = 0;
  return OTC_STAGES.map((st) => {
    const docs = (Object.values(OTC_DOCS))
      .filter((d) => d.stage === st.no)
      .filter((d) => d.docCode !== 'TMP081' && d.docCode !== 'TMP082')
      .map((d) => ({ ...d, comp: DOC_COMPONENTS[d.docCode], no: (n += 1) }));
    return { ...st, docs };
  });
});

/** allottee 개인/법인 자동판정 — 저장된 category('individual'/'corporate') 우선, 없으면 ID 추론. */
function categoryOf(n: number): 'individual' | 'corporate' {
  const stored = String(ctx.valueOf(`allottee_category_${n}`) || '').toLowerCase();
  if (stored === 'corporate' || stored === 'individual') return stored;
  return inferAllotteeCategory('', ctx.valueOf(`allottee_id_no_${n}`));
}
/** Stage 1 에서 이름이 입력된 allottee 마다 Subscription 문서 1개. */
const allotteeSubs = computed(() => {
  const out: { index: number; name: string; type: 'individual' | 'corporate' }[] = [];
  for (let n = 1; n <= ctx.allotteeCount.value; n++) {
    const name = String(ctx.valueOf(`allottee_name_${n}`) || '').trim();
    if (!name) continue;
    out.push({ index: n, name, type: categoryOf(n) });
  }
  return out;
});
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0) + allotteeSubs.value.length);
</script>

<template>
  <div ref="rootEl" class="allot-pkg">
    <!-- 단계 스텝퍼 -->
    <div class="stepper">
      <button
        v-for="st in view" :key="st.no"
        class="step" :class="{ active: currentStage === st.no, done: currentStage > st.no }"
        :title="`Stage ${st.no} · ${st.en}`" @click="go(st.no)"
      >
        <span class="step-no">{{ currentStage > st.no ? '✓' : st.no }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.en }}</small></span>
      </button>
    </div>

    <!-- 각 단계 (현재 단계만 표시 · 나머지는 마운트 유지) -->
    <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
      <div class="stage-head">
        <span class="stage-badge">Stage {{ st.no }}</span>
        <span class="stage-t">{{ st.en }}</span>
      </div>

      <!-- Stage 2: Subscription — Stage 1 allottee 1명당 1문서, 개인/법인 자동판정 -->
      <template v-if="st.no === 2">
        <div v-if="!allotteeSubs.length" class="sub-empty">
          Stage 1 에서 allottee 를 입력하면 각 allottee 별 청약 문서가 자동으로 생성됩니다.
        </div>
        <div v-for="s in allotteeSubs" :key="`sub-${s.index}`" class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">{{ s.type === 'corporate' ? '🏢' : '🙍' }}</span>
            <span class="nm">Subscription — {{ s.name }} <small class="sub-type">({{ s.type === 'corporate' ? 'Corporate' : 'Individual' }})</small></span>
          </div>
          <div class="pkg-paper"><OtcSubscriptionDoc :index="s.index" :type="s.type" /></div>
        </div>
      </template>

      <div v-for="d in st.docs" :key="d.docCode" class="pkg-doc">
        <div class="pkg-doclabel">
          <span class="ic">{{ d.icon }}</span>
          <span class="nm">{{ d.label }}</span>
          <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
        </div>
        <div class="pkg-paper"><component :is="d.comp" /></div>
      </div>
    </section>

    <!-- 단계 이동 -->
    <div class="stage-nav">
      <button v-if="currentStage > 1" class="nav-btn" @click="go(currentStage - 1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ OTC_STAGES.length }}</span>
      <button v-if="currentStage < OTC_STAGES.length" class="nav-btn primary" @click="go(currentStage + 1)">Next →</button>
      <button v-else class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
  </div>
</template>

<!-- 문서 공용 스타일 (전역) -->
<style src="./otcDoc.css"></style>

<style scoped>
.allot-pkg { color: #111; }
.sub-empty { max-width: 820px; margin: 8px auto 16px; padding: 14px 16px; background: #fff8e6; border: 1px dashed #f2c94c; border-radius: 10px; color: #7a5b00; font-size: 13px; }
.sub-type { color: #5a6b8c; font-weight: 600; }
.stepper { display: flex; gap: 8px; width: 100%; max-width: 900px; margin: 0 auto 18px; padding: 10px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20, 30, 60, .06); }
.step { flex: 1; display: flex; align-items: center; gap: 9px; padding: 9px 11px; border: 1px solid transparent; border-radius: 10px; background: #f6f8fc; cursor: pointer; text-align: left; transition: all .15s; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.step-txt b { font-size: 12px; color: #1f2d4d; }
.step-txt small { font-size: 10.5px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stage { width: 100%; max-width: 900px; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 10px; margin: 4px 0 14px; padding: 9px 4px; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 4px 11px; border-radius: 999px; letter-spacing: .02em; }
.stage-t { font-weight: 800; font-size: 14px; color: #1f2d4d; }
.subtype-bar { display: flex; align-items: center; gap: 12px; margin: 0 auto 16px; padding: 12px 14px; background: #fffaf0; border: 1px solid #f2e2b8; border-radius: 12px; }
.subtype-lbl { font-weight: 800; font-size: 12.5px; color: #7a5b1a; }
.subtype-seg { display: inline-flex; background: #fff; border: 1px solid #e5c98a; border-radius: 10px; overflow: hidden; }
.subtype-seg button { border: none; background: transparent; padding: 8px 16px; font-size: 12.5px; font-weight: 700; color: #8a6a2a; cursor: pointer; }
.subtype-seg button.on { background: #f0a83c; color: #fff; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doc + .pkg-doc { margin-top: 30px; }
.pkg-doclabel { display: flex; align-items: center; gap: 7px; padding: 0 4px 9px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .01em; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.pkg-paper { background: #fff; padding: 34px 38px; box-shadow: 0 4px 24px rgba(20, 30, 60, .12); font-family: Arial, 'Malgun Gothic', sans-serif; color: #111; font-size: 12px; line-height: 1.4; border-radius: 2px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 14px; width: 100%; max-width: 900px; margin: 26px auto 6px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 10px 18px; cursor: pointer; }
.nav-btn:hover:not(:disabled) { background: #f2f6fc; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn.primary:hover:not(:disabled) { background: #1550b8; }
.nav-btn:disabled { opacity: .55; cursor: default; }
.nav-count { font-size: 12px; font-weight: 800; color: #8a97b5; }
</style>
